import React from "react";
import { FiMenu } from "react-icons/fi";
import { LuRepeat } from "react-icons/lu";
import { Button } from "@heroui/react";
import { useAuth } from "@/contexts/AuthContext";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { CohortService } from "@/services/cohortService";
import LanguageSwitcher from "./LanguageSwitcher";
import { useLanguage } from "@/contexts/LanguageContext";
import { useHeaderStore } from "@/store/headerStore";
import { getTrainerActiveCohort } from "@/utils/session";
import { ROLES } from "@/constants/roles";
import ParticipantChatLauncher from "@/components/chat/ParticipantChatLauncher";
import TransactionViewSwitch from "./TransactionViewSwitch";

export interface HeaderProps {
  onToggleSidebar?: () => void;
}

const Header: React.FC<HeaderProps> = ({ onToggleSidebar }) => {
  const { isHeaderVisible } = useHeaderStore();
  const { currentUser } = useAuth();
  const { cohortId } = useParams<{ cohortId: string }>();
  const [cohortLanguage, setCohortLanguage] = React.useState<
    "english" | "french" | "spanish" | "german" | "italian" | "portuguese" | "dutch" | "hebrew" | "arabic" | "chinese" | undefined
  >();
  const { t } = useLanguage();
  const navigate = useNavigate();
  const location = useLocation();

  // Determine if trainer is in course view (either on /course/:id or on sub-pages with active cohort)
  const isTrainerCourseView = currentUser?.role === ROLES.TRAINER &&
    (!!cohortId || !!getTrainerActiveCohort());
  const activeCohortId = cohortId || getTrainerActiveCohort();

  React.useEffect(() => {
    const loadCohortLanguage = async () => {
      const cId = cohortId || (currentUser?.role === ROLES.TRAINER ? getTrainerActiveCohort() : null);
      if (cId) {
        const cohort = await CohortService.getCohortById(cId);
        if (cohort) {
          setCohortLanguage(cohort.language);
        }
      }
    };

    loadCohortLanguage();
  }, [cohortId, currentUser?.role]);

  // Check if the current path matches the tab
  const isActiveTab = (path: string) => {
    // For trainer course view, "Activity Area" maps to /course/:cohortId
    if (path === "/dashboard" && isTrainerCourseView) {
      return location.pathname.startsWith("/course/");
    }
    return location.pathname === path;
  };

  // Navigate to the selected tab
  const handleTabClick = (path: string) => {
    // For trainer course view, "Activity Area" should go back to /course/:cohortId
    if (path === "/dashboard" && isTrainerCourseView && activeCohortId) {
      navigate(`/course/${activeCohortId}`);
      return;
    }
    navigate(path);
  };

  return (
    <div
      className={`flex flex-col sm:flex-row items-center bg-white shadow-md gap-3 sm:gap-0 sticky top-0 z-30 transition-all duration-300 ease-in-out transform ${
        isHeaderVisible
          ? "translate-y-0 p-2 2xl:p-4"
          : "h-0 p-0 overflow-hidden -translate-y-full"
      }`}
    >
      <div>
        <FiMenu
          size={20}
          className="lg:hidden cursor-pointer"
          onClick={onToggleSidebar}
        />
      </div>
      <div className="flex sm:gap-2 lg:gap-6 justify-center w-full sm:w-auto sm:flex-1">
        <Button
          className={`px-3 py-2 text-sm font-medium border-none focus:outline-none ${
            isActiveTab("/dashboard")
              ? "bg-sky-600/10 text-sky-700 font-bold"
              : "bg-transparent text-[#98A2B3]"
          }`}
          onPress={() => handleTabClick("/dashboard")}
        >
          {t("navigation.activityArea")}
        </Button>

        <Button
          className={`px-3 py-2 text-sm font-medium border-none focus:outline-none ${
            isActiveTab("/financial-statements")
              ? "bg-sky-600/10 text-sky-700 font-bold"
              : "bg-transparent text-[#98A2B3]"
          }`}
          onPress={() => handleTabClick("/financial-statements")}
        >
          {t("navigation.financialStatements")}
        </Button>
      </div>
      <div className="max-w-md w-full sm:w-auto flex justify-end items-center gap-3">
        {/* Without a cohortId the switch resolves the participant's own cohort;
            the session fallback only applies to a trainer inside a course. */}
        <TransactionViewSwitch
          cohortId={
            cohortId || (isTrainerCourseView ? activeCohortId ?? undefined : undefined)
          }
        />
        {currentUser?.role === ROLES.USER && <ParticipantChatLauncher />}
        {isTrainerCourseView && (
          <Button
            isIconOnly
            aria-label="Switch View"
            title="Switch View"
            variant="bordered"
            onPress={() =>
              activeCohortId
                ? navigate(`/cohort-dashboard/${activeCohortId}`)
                : navigate("/active-cohorts")
            }
          >
            <LuRepeat size={20} />
          </Button>
        )}
        <img
          src="/assets/images/logo/logo.png"
          alt="AME Logo"
          className="h-8 lg:h-10 xl:h-12 w-auto"
        />
      </div>
      {/* <div className="flex items-center gap-4">
        <LanguageSwitcher cohortLanguage={cohortLanguage} />
      </div> */}
    </div>
  );
};

export default Header;
